import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { getPathWithRef } from '../utils/attribution';
import LanguageSwitch from '../components/LanguageSwitch';
import { SkeletonDashboard } from '../components/advisor/SkeletonLoader';

/**
 * User dashboard: matches application-workspace v1.html design.
 * Start/continue session, completion, specialist, bottom nav. EN/ES via LanguageSwitch.
 */
export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [completionData, setCompletionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [agentAvailability, setAgentAvailability] = useState({
    hasAvailable: false,
    availableCount: 0,
    totalOnlineCount: 0,
    estimatedWaitTime: null,
  });
  const [loadError, setLoadError] = useState(null);
  const availabilityAbortRef = useRef(null);

  const checkAgentAvailability = useCallback(async (signal) => {
    try {
      const response = await mvpApi.checkAgentAvailability(signal);
      if (response.data) {
        setAgentAvailability({
          hasAvailable: response.data.has_available_agents,
          availableCount: response.data.available_count,
          totalOnlineCount: response.data.total_online_count,
          estimatedWaitTime: response.data.estimated_wait_time,
        });
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      setAgentAvailability({
        hasAvailable: false,
        availableCount: 0,
        totalOnlineCount: 0,
        estimatedWaitTime: null,
      });
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate(getPathWithRef('/'), { replace: true });
        return;
      }
      const lang = localStorage.getItem('i18nextLng') || 'es';
      const sessionResponse = await mvpApi.startSession({
        language: lang,
        form_type: 'UNIFIED',
      });
      if (sessionResponse.data && sessionResponse.data.success) {
        const sessionId = sessionResponse.data.session_id;
        setSessionData(sessionResponse.data);
        localStorage.setItem('immigrationSessionId', sessionId);
        try {
          const dataResponse = await mvpApi.getCollectedData(sessionId);
          if (dataResponse.data) setCompletionData(dataResponse.data);
        } catch {
          setCompletionData({
            completed_fields: [],
            missing_fields: [],
            completion_percentage: 0,
            total_fields: 54,
          });
        }
      } else {
        setCompletionData({
          completed_fields: [],
          missing_fields: [],
          completion_percentage: 0,
          total_fields: 54,
        });
      }
    } catch (err) {
      if (err.response?.status === 401) navigate(getPathWithRef('/'), { replace: true });
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.code === 'ERR_NETWORK') {
        setLoadError('backend_unavailable');
        setCompletionData({
          completed_fields: [],
          missing_fields: [],
          completion_percentage: 0,
          total_fields: 54,
        });
        setSessionData(null);
      } else {
        setLoadError(null);
        setCompletionData({
          completed_fields: [],
          missing_fields: [],
          completion_percentage: 0,
          total_fields: 54,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    const doCheck = () => {
      if (availabilityAbortRef.current) availabilityAbortRef.current.abort();
      availabilityAbortRef.current = new AbortController();
      checkAgentAvailability(availabilityAbortRef.current.signal);
    };
    doCheck();
    const interval = setInterval(doCheck, 10000);
    return () => {
      clearInterval(interval);
      if (availabilityAbortRef.current) availabilityAbortRef.current.abort();
    };
  }, [checkAgentAvailability]);

  const handleContinueApplication = () => {
    const sid = sessionData?.session_id;
    if (sid) localStorage.setItem('immigrationSessionId', sid);
    navigate('/chat');
  };

  const handleSpecialistChat = () => {
    const sid = sessionData?.session_id;
    if (sid) localStorage.setItem('immigrationSessionId', sid);
    navigate('/chat');
  };

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch {
      return {};
    }
  })();
  const userName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.email?.split('@')[0] ||
    'User';
  const userInitials =
    userName.split(' ').length >= 2
      ? userName.split(' ').map((p) => p[0]).join('').substring(0, 2).toUpperCase()
      : userName.substring(0, 2).toUpperCase();
  const firstName = userName.split(' ')[0] || '';
  const restName = userName.split(' ').slice(1).join(' ');

  const completedCount = completionData?.completed_fields?.length || 0;
  const missingCount = completionData?.missing_fields?.length || 0;
  const totalFields = completionData?.total_fields || (completedCount + missingCount) || 54;
  const completionPct = completionData?.completion_percentage ?? 0;

  const deadline = new Date('2026-06-30');
  const daysUntilDeadline = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));

  const todoItems = [
    { key: 'personal', name: t('dashboard.personalDetails'), sub: t('dashboard.personalDetailsSub'), status: completedCount > 0 ? 'done' : 'active', icon: '✓' },
    { key: 'residence', name: t('dashboard.residenceInfo'), sub: t('dashboard.residenceInfoSub'), status: 'pending', icon: '📋' },
    { key: 'passport', name: t('dashboard.passportId'), sub: t('dashboard.passportIdSub'), status: 'pending', icon: '🛂' },
    { key: 'municipal', name: t('dashboard.municipalReg'), sub: t('dashboard.municipalRegSub'), status: 'pending', icon: '🏠' },
    { key: 'criminal', name: t('dashboard.criminalCheck'), sub: t('dashboard.criminalCheckSub'), status: 'pending', icon: '📄' },
  ];

  if (isLoading) {
    return <SkeletonDashboard />;
  }

  return (
    <div className="mvp-shell">
      {loadError === 'backend_unavailable' && (
        <div style={{
          padding: '12px 16px',
          margin: '0 16px 12px',
          background: 'rgba(220, 53, 69, 0.15)',
          border: '1px solid rgba(220, 53, 69, 0.5)',
          borderRadius: '8px',
          color: 'var(--muted)',
          fontSize: '0.9rem',
        }}>
          Backend is not responding. Make sure the API is running on port 8000 and try again.
        </div>
      )}
      <div className="mvp-app">
        <div className="topbar">
          <div className="topbar-brand">
            <svg className="topbar-logo" viewBox="0 0 100 100" fill="none" width="30" height="30">
              <path d="M 85,18 A 44,44 0 1,0 86,82" stroke="#5189A0" strokeWidth="13" strokeLinecap="round" />
              <path d="M 62,50 L 86,50 L 86,74" stroke="#5189A0" strokeWidth="13" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="topbar-name">{t('brandName')}</div>
          </div>
          <div className="topbar-right">
            <LanguageSwitch />
            <div className="topbar-notif">
              🔔
              <div className="notif-badge" />
            </div>
            <button
              type="button"
              className="topbar-avatar topbar-avatar-btn"
              onClick={() => navigate('/account')}
              aria-label={t('dashboard.nav.profile')}
            >
              {userInitials}
            </button>
          </div>
        </div>

        <div className="scroll-area">
          <div className="greeting adv-animate-fade-in">
            <div className="greeting-hi">{t('dashboard.welcomeBack')}</div>
            <h1 className="greeting-name">{firstName} <span className="text-emerald-400">{restName}</span></h1>
          </div>


          <div className="program-card adv-modern-card adv-animate-fade-in !bg-slate-900 !border-slate-800 shadow-2xl overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-emerald-500/20 transition-all duration-700" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full -ml-12 -mb-12 blur-xl" />
            
            <div className="relative z-10">
              <div className="pc-label !text-emerald-400 !font-bold !tracking-widest">{t('dashboard.yourActiveProgram')}</div>
              <div className="pc-title !text-xl !font-black !mb-2">{t('dashboard.programTitle')}</div>
              <div className="pc-sub !text-slate-400 !mb-6">{t('dashboard.programSub')}</div>
              
              <div className="pc-progress-label !mb-2">
                <span className="text-slate-300 font-medium">{t('dashboard.progress')}</span>
                <span className="text-emerald-400 font-bold">{t('dashboard.ofCompleted', { count: completedCount, total: totalFields })}</span>
              </div>
              
              <div className="pc-track !h-2.5 !bg-slate-800 !rounded-full !mb-6 shadow-inner">
                <div 
                  className="pc-fill !h-full !bg-gradient-to-right from-emerald-500 to-teal-400 !rounded-full shadow-[0_0_12px_rgba(16,185,129,0.4)] transition-all duration-1000 ease-out" 
                  style={{ width: `${Math.max(0, completionPct)}%` }} 
                />
              </div>
              
              <div className="flex items-center justify-between gap-4">
                <button 
                  type="button" 
                  className="pc-btn !bg-emerald-500 !text-white !rounded-xl !px-6 !py-3 !text-sm !font-bold hover:!bg-emerald-400 hover:!scale-[1.02] active:!scale-[0.98] transition-all shadow-lg shadow-emerald-900/20 flex items-center gap-2" 
                  onClick={handleContinueApplication}
                >
                  {t('dashboard.continueApplication')} <i className="ri-arrow-right-line" />
                </button>
                
                <div className="pc-deadline text-right">
                  <div className="pc-deadline-date !text-amber-400 !font-bold !text-base">Jun 30</div>
                  <div className="pc-deadline-lbl !text-[10px] !text-slate-500 !font-bold !uppercase !tracking-wider">{t('dashboard.deadline')}</div>
                </div>
              </div>
            </div>
          </div>


          <div className="sec-head">
            <div className="sec-title">{t('dashboard.yourTodoList')}</div>
            <div className="sec-link">{t('dashboard.seeAll')}</div>
          </div>

          <div className="checklist">
            {todoItems.map((item, idx) => (
              <div 
                key={item.key} 
                className={`adv-modern-card adv-animate-fade-in !p-4 flex items-center gap-4 mb-3 border !border-slate-800 ${item.status === 'done' ? 'opacity-60 grayscale-[0.5]' : 'bg-slate-900/50 backdrop-blur-sm'}`}
              >
                <div className={`w-12 h-12 rounded-2xl shrink-0 flex items-center justify-center text-xl shadow-inner ${
                  item.status === 'done' ? 'bg-emerald-500/20 text-emerald-400' : 
                  item.status === 'active' ? 'bg-blue-500/20 text-blue-400' : 
                  'bg-slate-800 text-slate-500'
                }`}>
                  {item.status === 'done' ? <i className="ri-checkbox-circle-fill" /> : item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-100 mb-0.5">{item.name}</div>
                  <div className="text-xs text-slate-500 truncate">{item.sub}</div>
                </div>
                <div className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  item.status === 'done' ? 'bg-emerald-500/10 text-emerald-400' : 
                  item.status === 'active' ? 'bg-blue-500/10 text-blue-400' : 
                  'bg-slate-800 text-slate-500'
                }`}>
                  {item.status === 'done' ? t('dashboard.done') : item.status === 'active' ? t('dashboard.active') : t('dashboard.pending')}
                </div>
              </div>
            ))}

          </div>

          <div className="info-row adv-animate-fade-in !gap-4">
            <div className="adv-modern-card !p-4 !bg-slate-900/50 !border-slate-800 flex flex-col gap-1">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500 mb-2">
                 <i className="ri-calendar-event-line" />
              </div>
              <div className="text-xl font-black text-amber-400 tabular-nums">{daysUntilDeadline} <span className="text-xs font-bold uppercase">{t('dashboard.days')}</span></div>
              <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{t('dashboard.untilDeadline')}</div>
            </div>
            <div className="adv-modern-card !p-4 !bg-slate-900/50 !border-slate-800 flex flex-col gap-1">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500 mb-2">
                 <i className="ri-attachment-line" />
              </div>
              <div className="text-xl font-black text-slate-100 tabular-nums">{completedCount} / {totalFields}</div>
              <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{t('dashboard.docsUploaded')}</div>
            </div>
          </div>


          {/* Your specialist bar - hidden for now
          <div className="sec-head">
            <div className="sec-title">{t('dashboard.yourSpecialist')}</div>
          </div>
          <div className="specialist-card">
            <div className="spec-avatar">
              AG
              <div className="spec-online" />
            </div>
            <div className="spec-body">
              <div className="spec-name">Global Mobility</div>
              <div className="spec-role">{t('dashboard.specialistRole')}</div>
              <div className="spec-status">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: agentAvailability.hasAvailable ? 'var(--success)' : agentAvailability.totalOnlineCount > 0 ? 'var(--warn)' : 'var(--muted)' }} />
                {agentAvailability.hasAvailable ? t('dashboard.availableNow') : agentAvailability.totalOnlineCount > 0 ? t('dashboard.busy') : t('dashboard.offline')}
              </div>
            </div>
            <button type="button" className="spec-chat-btn" onClick={handleSpecialistChat}>
              {t('dashboard.chat')}
            </button>
          </div>
          */}
        </div>

        <nav className="bottom-nav">
          <button type="button" className="nav-item nav-active" aria-current="page">
            <span className="nav-icon">🏠</span>
            <span className="nav-label">{t('dashboard.nav.home')}</span>
          </button>
          <button type="button" className="nav-item" onClick={() => navigate('/chat')}>
            <span className="nav-icon">📋</span>
            <span className="nav-label">{t('dashboard.nav.application')}</span>
          </button>
          <button type="button" className="nav-center-btn">
            <div className="nav-center-inner">
              <svg width="22" height="22" viewBox="0 0 100 100" fill="none">
                <path d="M 85,18 A 44,44 0 1,0 86,82" stroke="white" strokeWidth="14" strokeLinecap="round" />
                <path d="M 62,50 L 86,50 L 86,74" stroke="white" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="nav-center-lbl">{t('dashboard.nav.global')}</span>
          </button>
          {/* Talk to agent / specialist chat - commented out
          <button type="button" className="nav-item" onClick={handleSpecialistChat}>
            <span className="nav-icon">💬</span>
            <span className="nav-label">{t('dashboard.nav.support')}</span>
          </button>
          */}
          <button type="button" className="nav-item" onClick={() => navigate('/account')}>
            <span className="nav-icon">👤</span>
            <span className="nav-label">{t('dashboard.nav.profile')}</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
